/**
 * Watched-items registry — stream URLs the user has finished (or at least
 * started) watching. Scoped to the active profile and persisted in
 * localStorage. Drives the green "✓ watched" overlay on Movie tiles and
 * Series episode rows in Browse.jsx.
 *
 * NOTE: we use the stream URL as the canonical key because that's the
 * one identifier every content type (Xtream, M3U, B2C) carries. Series
 * favorites use a `series://...` synthetic URL but we only track
 * individual *episodes* here — never an aggregate series.
 */
import { getActiveProfileId } from "./profiles";
import { scheduleProfileCacheSync } from "./profileCacheSync";

const BASE_KEY = "vupp_watched_items_v1";

// Subscribers that want to re-render when the watched set changes (Browse
// tiles, episode lists, etc.). Lightweight pub-sub so we don't force a
// global Context re-render on every play.
const listeners = new Set();
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notify() {
  for (const fn of listeners) {
    try { fn(); } catch { /* one bad listener shouldn't break the rest */ }
  }
}

function storageKey() {
  const pid = getActiveProfileId();
  return pid ? `${BASE_KEY}__${pid}` : BASE_KEY;
}

function readSet() {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch {
    return new Set();
  }
}

function writeSet(set) {
  try {
    localStorage.setItem(storageKey(), JSON.stringify([...set]));
  } catch { /* quota exceeded — silently drop */ }
  notify();
  scheduleProfileCacheSync();
}

/**
 * Mark a stream URL as watched. Only persists for VOD content
 * (movie / series / episode) — Live TV would be meaningless because the
 * stream URL is the channel, not a piece of content.
 */
export function markWatched(streamUrl, contentType) {
  if (!streamUrl) return;
  const t = (contentType || "").toLowerCase();
  if (t === "live_tv" || t === "live" || t === "catchup") return;
  const set = readSet();
  if (set.has(streamUrl)) return; // already there — no write, no notify
  set.add(streamUrl);
  writeSet(set);
}

/**
 * v1.4.30 — Bulk mark helper for the "Mark season / show as watched"
 * context-menu actions. Skips falsy entries and live-TV / catchup
 * URLs (same rule as `markWatched`).
 */
export function markManyWatched(streamUrls, contentType) {
  if (!Array.isArray(streamUrls) || streamUrls.length === 0) return 0;
  const t = (contentType || "").toLowerCase();
  if (t === "live_tv" || t === "live" || t === "catchup") return 0;
  const set = readSet();
  let added = 0;
  for (const url of streamUrls) {
    if (!url) continue;
    if (!set.has(url)) {
      set.add(url);
      added++;
    }
  }
  if (added === 0) return 0;
  writeSet(set);
  return added;
}

export function unmarkWatched(streamUrl) {
  if (!streamUrl) return;
  const set = readSet();
  if (!set.delete(streamUrl)) return;
  writeSet(set);
}

/**
 * v1.4.30 — Bulk unmark helper for "Mark season / show as unwatched".
 */
export function unmarkManyWatched(streamUrls) {
  if (!Array.isArray(streamUrls) || streamUrls.length === 0) return 0;
  const set = readSet();
  let removed = 0;
  for (const url of streamUrls) {
    if (!url) continue;
    if (set.delete(url)) removed++;
  }
  if (removed === 0) return 0;
  writeSet(set);
  return removed;
}

export function isWatched(streamUrl) {
  if (!streamUrl) return false;
  return readSet().has(streamUrl);
}

/** Returns a fresh Set — callers may mutate freely without side-effects. */
export function getAllWatched() {
  return readSet();
}

export function clearWatched() {
  try {
    localStorage.removeItem(storageKey());
  } catch { /* noop */ }
  notify();
  scheduleProfileCacheSync();
}
